import { PromptType } from '@communities/prompt-management/domain';
import { usePromptDetails, useUpdateProdPromptVersion } from '@communities/prompt-management/hooks';
import { formatPromptVersion } from '@communities/prompt-management/utils';
import { ErrorBanner } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Dialog, LoadingSpinner, Spinner } from '@soteria/design-system-ve';
import { useEffect, useState } from 'react';

import { PromptTypeTabs } from './PromptTypeTabs';
import * as styles from './ViewPromptDialog.css.ts';

interface ViewPromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  promptVersion?: string;
  isProductionPrompt: boolean;
  communityId?: string;
  isCommunityAdmin?: boolean;
}

/**
 * View Prompt Dialog
 */
// eslint-disable-next-line complexity
function ViewPromptDialogComponent({ isOpen, onClose, promptVersion, isProductionPrompt, communityId, isCommunityAdmin }: ViewPromptDialogProps) {
  const isModerationEnabled = useIsFeatureEnabled(FEATURE_FLAGS.MODERATION);
  const [activeTab, setActiveTab] = useState<PromptType>(PromptType.CLASSIFICATION);

  const classificationInput = promptVersion ? { promptVersion, type: PromptType.CLASSIFICATION } : undefined;
  const {
    promptDetails: classificationDetails,
    loading: classificationLoading,
    errors: classificationErrors,
  } = usePromptDetails(communityId, classificationInput);

  const moderationInput = activeTab === PromptType.MODERATION && promptVersion ? { promptVersion, type: PromptType.MODERATION } : undefined;
  const { promptDetails: moderationDetails, loading: moderationLoading, errors: moderationErrors } = usePromptDetails(communityId, moderationInput);

  const { updatePromptProdVersion, loading: isUpdating, errors: updateErrors, resetErrors } = useUpdateProdPromptVersion();

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetErrors();
      setActiveTab(PromptType.CLASSIFICATION);
    }
  }, [isOpen, setActiveTab, resetErrors]);

  // Handle tab change
  const handleTabChange = (tab: PromptType) => {
    setActiveTab(tab);
  };

  const handleMakeProduction = async () => {
    resetErrors();

    if (communityId && promptVersion) {
      await updatePromptProdVersion(communityId, { version: promptVersion });
    }
  };

  if (!promptVersion) {
    return null;
  }

  const customHeader = (
    <div>
      {/* Title with production badge */}
      <div className={styles.headerTitleContainer}>
        <h2 className={styles.headerTitle}>Version {promptVersion}</h2>
        {isProductionPrompt && <span className={styles.productionBadge}>PRODUCTION</span>}
      </div>
      {/* Created info */}
      <div className={styles.createdInfo}>Created {formatPromptVersion(promptVersion)}</div>
    </div>
  );

  return (
    <Dialog
      cancelAction={{
        label: 'Close',
        disabled: false,
      }}
      customHeader={customHeader}
      isOpen={isOpen}
      primaryAction={
        !isProductionPrompt && isCommunityAdmin
          ? {
              label: isUpdating ? <LoadingSpinner /> : 'Make Production',
              onClick: handleMakeProduction,
              disabled: isUpdating,
              variant: 'accent',
            }
          : undefined
      }
      size="xl"
      onClose={onClose}
    >
      <div className={styles.promptDetails}>
        {/* Error Banner */}
        {updateErrors && updateErrors.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorBanner errors={updateErrors} title="Failed to update prompt:" />
          </div>
        )}
        {/* Tabs - only show when moderation is enabled */}
        {isModerationEnabled && <PromptTypeTabs activeTab={activeTab} onTabChange={handleTabChange} />}

        {/* Content based on active tab or feature flag state */}
        <div className={styles.promptSection}>
          {!isModerationEnabled || activeTab === PromptType.CLASSIFICATION ? (
            classificationLoading ? (
              <div className={styles.loadingContainer}>
                <Spinner size="medium" />
              </div>
            ) : classificationErrors && classificationErrors.length > 0 ? (
              <div className={styles.errorContainer}>
                <p>Failed to load classification prompt</p>
              </div>
            ) : (
              <div className={styles.promptContent}>
                <pre className={styles.promptCodeBlock}>{classificationDetails?.prompt}</pre>
              </div>
            )
          ) : moderationLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner size="medium" />
            </div>
          ) : moderationErrors && moderationErrors.length > 0 ? (
            <div className={styles.errorContainer}>
              <p>Failed to load moderation prompt</p>
            </div>
          ) : (
            <div className={styles.promptContent}>
              <pre className={styles.promptCodeBlock}>{moderationDetails?.prompt || 'No moderation prompt available'}</pre>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export const ViewPromptDialog = withAppInsightsTracking(ViewPromptDialogComponent, 'ViewPromptDialog');
